import { FunctionalComponent, h, JSX } from '@stencil/core/internal'
import { css, keyframes } from 'src/lib/emotion'

import { ICue, ICueSpan, INoteClip } from '../../../call/types'
import { colors as themeColors } from '../../../tailwind'
import { formatClipDuration } from '../../../utils/clip'
import { classes, colorClasses } from '../../../utils/css'

export const SpeakerAvatar: FunctionalComponent<
  { name: string; email: string } & JSX.IntrinsicElements['ui-gravatar']
> = ({ name, email, class: className, ...attrs }) => {
  return (
    <ui-gravatar
      class={`
        rounded-full flex-shrink-0
        ${className}
      `}
      email={email}
      initials={name
        .split(' ')
        .slice(0, 2)
        .reduce((result, s) => result + s.charAt(0).toUpperCase(), '')}
      {...attrs}
    >
      <ui-tooltip>{name}</ui-tooltip>
    </ui-gravatar>
  )
}

const move = keyframes`
0% {
  background-position: 0 0;
}
100% {
  background-position: 5.66px 0;
}
`

const processing = css`
  background-image: linear-gradient(
    135deg,
    ${themeColors['surface-high']} 25%,
    transparent 25%,
    transparent 50%,
    ${themeColors['surface-high']} 50%,
    ${themeColors['surface-high']} 75%,
    transparent 75%,
    transparent 100%
  );
  background-size: 5.66px 5.66px;
  background-attachment: fixed;
  animation: ${move} 0.5s linear infinite;
`

export const ProcessingSpan: FunctionalComponent<{
  pct: number
}> = ({ pct }) => {
  return (
    pct > 0 && (
      <div
        class={`h-2 rounded-r-full ${processing}`}
        style={{
          left: `${(1 - pct) * 100}%`,
          width: `${pct * 100}%`,
        }}
      ></div>
    )
  )
}

export const NoteSpan: FunctionalComponent<{
  noteClip: INoteClip
  left: number
  width: number
  isPending: boolean
  onClick?: () => void
  onMouseOver: () => void
  onMouseOut: () => void
}> = ({ noteClip, left, width, isPending, onClick, onMouseOver, onMouseOut }) => {
  return (
    <div
      key={noteClip.id}
      class={`
        z-10 absolute transition duration-200 rounded-full cursor-pointer
        ${classes(isPending, 'h-1 opacity-0 pointer-events none', 'h-3')}
        ${colorClasses({ bg: noteClip.note.bookmark.hex_color })}
      `}
      style={{ left: `${left * 100}%`, width: `${width * 100}%`, minWidth: '15px' }}
      onClick={(e) => {
        if (onClick) {
          e.stopImmediatePropagation()
          onClick()
        }
      }}
      onMouseOver={() => onMouseOver()}
      onMouseOut={() => onMouseOut()}
    >
      <ui-tooltip bgColor='surface-middle'>
        <div class='flex flex-col items-center spacing-y-1' style={{ minWidth: '15ch' }}>
          <span
            class={`uppercase leading-none font-bold whitespace-no-wrap text-base ${colorClasses({
              text: noteClip.note.bookmark.hex_color,
            })}`}
          >
            {noteClip.note.bookmark.label}
          </span>
          <hr class='w-full border-b border-surface-high' />
          <span class='text-center'>{`${noteClip.speakers.join(', ')}, ${formatClipDuration(
            noteClip.duration_seconds
          )}`}</span>
        </div>
      </ui-tooltip>
    </div>
  )
}

export const CueSpan: FunctionalComponent<{ cueSpan: ICueSpan; left: number; width: number; onClick?: () => void }> = ({
  cueSpan,
  left,
  width,
  onClick,
}) => {
  return (
    <div
      key={cueSpan.id}
      class={`
        absolute transition hover:bg-opacity-50 h-2
        ${classes(cueSpan.type === 'guest', 'bg-white bg-opacity-25')}
        ${classes(left + width <= 0.01 || left >= 0.99, 'hidden')}
        ${classes(left < 0.01, 'rounded-l-full')}
        ${classes(left + width > 0.99, 'rounded-r-full')}
      `}
      style={{ left: `${left * 100}%`, width: `${width * 100}%` }}
      onClick={(e) => {
        if (onClick) {
          e.stopImmediatePropagation()
          onClick()
        }
      }}
    >
      <ui-tooltip bgColor='surface-middle'>
        <div class='flex flex-col items-center spacing-y-1' style={{ minWidth: '15ch' }}>
          <span>{`${cueSpan.speakers.join(', ')}, ${formatClipDuration(cueSpan.duration_seconds)}`}</span>
        </div>
      </ui-tooltip>
    </div>
  )
}

export const SearchMarker: FunctionalComponent<{
  cue: ICue
  found: boolean
  left: number
  onClick: () => void
}> = ({ cue, found, left, onClick }) => {
  return (
    <div
      key={cue.id}
      class={`
        absolute flex w-2 mt-3 bg-white cursor-pointer bg-opacity-0 hover:z-10 hover:opacity-100
        ${classes(found, 'z-10', 'opacity-50')}
      `}
      style={{ left: `${left * 100}%` }}
      onClick={onClick}
    >
      <ui-icon class='z-10 -ml-1 text-xl text-link pointer-events-none' icon='caret-up' />
      <ui-icon class='absolute top-0 left-0 -mt-3px -ml-7px text-3xl text-black pointer-events-none' icon='caret-up' />
    </div>
  )
}
